import React, { createContext, useContext, useState, useEffect } from 'react';
import { returnLoginUrl, returnLogoutUrl } from 'utils/baseURL';
import { environment } from 'environments/environment';
import { jwtDecode } from 'jwt-decode';
import { APP_SYSTEM_PATH } from 'App';

export interface AuthContextType {
  token: string | null;
  setUrlToken: (jwt_token: string) => void;
  clearToken: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  login: () => void;
  user: any;
  getToken: () => any;
}

const SITE_ENVIRONMENT = environment.environment;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const CHAVE_TOKEN = 'jwt_token';
  const CHAVE_REDIRECT = 'auth_redirect';

  const [token, setTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isLocal = SITE_ENVIRONMENT === 'local';

  const logout = () => {
    setToken(null);
    setUrlRedirect(null);

    window.location.href = returnLogoutUrl(SITE_ENVIRONMENT);
  };

  const login = () => {
    setUrlRedirect(APP_SYSTEM_PATH);
    window.location.href = returnLoginUrl(SITE_ENVIRONMENT);
  };

  const setUrlToken = (urlToken: string) => {
    if (isLocal) {
      setToken(urlToken);
    }
  };

  const clearToken = () => {
    setToken(null);
  };

  const setToken = (newToken: string | null) => {
    if (newToken) {
      localStorage.setItem(CHAVE_TOKEN, newToken);
      setTokenState(newToken);
    } else {
      localStorage.removeItem(CHAVE_TOKEN);
      setTokenState(null);
    }
  };

  const setUrlRedirect = (redirect: string | null) => {
    if (redirect && isLocal === false) {
      localStorage.setItem(CHAVE_REDIRECT, redirect);
    } else {
      localStorage.removeItem(CHAVE_REDIRECT);
    }
  };

  const getToken = () => {
    if (isLoading === false && user) {
      return token;
    } else {
      return getTokenFromStorage();
    }
  };

  const getTokenFromStorage = () => {
    let user = null;
    const storedToken = localStorage.getItem(CHAVE_TOKEN);

    if (storedToken) {
      user = jwtDecode(storedToken);
      setTokenState(storedToken);
      setUser(user);
    }

    setIsLoading(false);

    return storedToken;
  };

  useEffect(() => {
    getTokenFromStorage();
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setUrlToken,
        clearToken,
        isAuthenticated: !!token,
        isLoading,
        logout,
        login,
        user,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  return context;
};
